import {
	IonInput,
	IonDatetime,
	IonCheckbox,
	IonLabel,
	IonSelect,
	IonSelectOption,
	IonRange,
} from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectOption } from "../../../interfaces";
import { isNil } from "ramda";
import "../forms.css";
import "./styles.css";

export type InputType =
	| "text"
	| "email"
	| "password"
	| "date"
	| "checkbox"
	| "select"
	| "number"
	| "range-bar";

export interface RangeBarProps {
	min: number;
	max: number;
	step: number;
}

interface InputFormProps {
	id: string;
	type?: InputType;
	label?: any;
	placeholder?: string;
	value?: any;
	defaultValue?: any;
	options?: SelectOption[];
	error?: string | false | undefined;
	disabled?: boolean;
	maxDate?: string;
	message?: string;
	className?: string;
	rangeProps?: RangeBarProps;
	onBlur?: (id: string) => void;
	onChange?: (id: string, value: string | boolean) => void;
	onValueChange?: (value: string | boolean | number) => void;
}

const InputForm: React.FC<InputFormProps> = ({
	id,
	type = "text",
	label,
	placeholder,
	value,
	defaultValue,
	options,
	error,
	disabled,
	maxDate,
	message,
	rangeProps,
	onBlur,
	onChange,
	onValueChange,
	className = "",
}) => {
	const [isFocus, setIsFocus] = useState(false);
	const { t, i18n } = useTranslation();

	return (
		<div className={`input-form input-form-${type} ${className}`}>
			{label && type !== "checkbox" && (
				<span className="input-label">{label}</span>
			)}
			{type === "range-bar" && (
				<span>
					:
					<strong>
						{" "}
						{(!isNil(value) && !isNaN(value) ? value : defaultValue) || 0} %
					</strong>
				</span>
			)}
			<div
				className={`input-outside-container ${
					type === "checkbox" ? "input-row nb" : "simple-input"
				} ${error ? "input-error" : ""} ${!label ? "without-label" : ""} ${
					isFocus ? "focus" : ""
				}`}
			>
				{type === "date" && (
					<IonDatetime
						displayFormat="DD/MM/YYYY"
						id={id}
						placeholder={placeholder}
						value={value}
						onIonChange={(e) => onChange!(id, e.detail.value!)}
						onIonBlur={() => {
							setIsFocus(false);
							onBlur && onBlur(id);
						}}
						onFocus={() => setIsFocus(true)}
						disabled={disabled}
						max={maxDate}
						className="input-value"
					/>
				)}
				{(type === "password" ||
					type === "text" ||
					type === "email" ||
					type === "number") && (
					<IonInput
						id={id}
						type={type}
						placeholder={placeholder}
						value={value}
						onIonChange={(e) => onValueChange!(e.detail.value!)}
						onIonBlur={() => {
							setIsFocus(false);
							onBlur && onBlur(id);
						}}
						disabled={disabled}
						clearInput
						onFocus={() => setIsFocus(true)}
						className="input-value"
					/>
				)}
				{type === "checkbox" && (
					<>
						<IonCheckbox
							mode="md"
							className="pix-checkbox mt-2"
							id={id}
							checked={value}
							onIonChange={(e) => onChange!(id, e.detail.checked)}
							onIonBlur={() => {
								setIsFocus(false);
								onBlur && onBlur(id);
							}}
							disabled={disabled}
							onFocus={() => setIsFocus(true)}
						/>
						<IonLabel className="ml-10">{label}</IonLabel>
					</>
				)}
				{type === "select" && (
					<>
						<IonSelect
							style={{
								color: "rgba(0, 0, 0, 0.8)",
								fontWeight: 400,
								fontFamily: "Roboto",
								paddingRight: "6px",
								paddingLeft: "8px",
							}}
							value={value}
							okText={t("general.ok")}
							cancelText={t("general.cancel")}
							onIonChange={(e) => onValueChange!(e.detail.value)}
						>
							{options?.map((opt) => (
								<IonSelectOption key={opt.key} value={opt.key}>
									{opt.label}
								</IonSelectOption>
							))}
						</IonSelect>
					</>
				)}
				{type === "range-bar" && (
					<>
						<IonRange
							style={{ paddingTop: "8px" }}
							value={!isNil(value) && !isNaN(value) ? value : defaultValue}
							min={rangeProps?.min}
							max={rangeProps?.max}
							step={rangeProps?.step}
							pin={true}
							onIonChange={(e) => onValueChange!(e.detail.value as number)}
						></IonRange>
					</>
				)}
			</div>
			{message && !error && <div className="text-message">{message}</div>}
			{error && <div className="text-error">{t(error)}</div>}
		</div>
	);
};

export default InputForm;
